/**
 * array.ts
 * ----------------
 * Utility functions for arrays.
 */

/**
 * Checks whether two arrays are set-equivalent to one another. Duplicates are ignored.
 * @param firstArray First array
 * @param secondArray Second array
 * @param eq Optional function for equivalence. Default is === operator.
 * @returns Whether the two arrays are set equivalent.
 */
export function isSetEquivalent<TItem> (
    firstArray: TItem[],
    secondArray: TItem[],
    eq?: (a: TItem, b: TItem) => boolean
) {
    // Quick exit: if both are empty, return true
    if (firstArray.length <= 0 && secondArray.length <= 0) {
        return true;
    }

    const firstSet = [...new Set(firstArray)];
    const secondSet = [...new Set(secondArray)];

    // Quick exit: if unequal in size, they cannot be equivalent
    if (firstSet.length !== secondSet.length) {
        return false;
    }

    // Set default equality function, which is strict equality
    const equalityFn = eq === undefined ? (a: TItem, b: TItem) => a === b : eq;

    for (const item of firstSet) {
        // Note: if this is used on large arrays, consider sorting or something to avoid a linear lookup
        if (secondSet.find(secondItem => equalityFn(item, secondItem)) === undefined) {
            return false;
        }
    }

    return true;
}
